import {
  faArchive,
  faBars,
  faCheckCircle,
  faCircleNotch,
  faCloudDownloadAlt,
  faEdit,
  faFileImport,
  faFont,
  faTrash,
  faTrashRestore,
  faEye,
  faLocationArrow,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TooltipContext } from '@stormwater-software/lib-ui';
import { addDays, parse, isBefore, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd7';
import { useDropzone } from 'react-dropzone';
import AppContext from '../../../contexts/AppContext';
import {
  FileSizeValidation,
  FileTypeValidation,
  ImageDimensionValidation,
  UploadFileValidation,
} from '../../../utils/file';
import SuccessModal from '../../Shared/SuccessModal/SuccessModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import Dropdown, { MenuItem } from '../Dropdown';

const DATE_FORMAT = 'MM-dd-yyyy';
const MIME_TYPE_STRIP_REGEX = /\/([a-z-]+)/;

const uploadFileValidation = UploadFileValidation({
  fileSizeValidation: FileSizeValidation(),
  fileTypeValidation: FileTypeValidation(),
  imageDimensionValidation: ImageDimensionValidation(),
});

const UploadState = Object.freeze({
  NOOP: 'NOOP',
  WAITING: 'WAITING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
});

const getUTCTime = (date) => {
  return toZonedTime(new Date(date), 'UTC');
};

export default function FileRow({
  permCheck,
  label,
  canDownload,
  downloadUrl,
  handleEdit,
  handleRename,
  canReplace,
  canRename,
  canDelete,
  canEdit,
  handleReplace,
  document,
  handleDelete,
  handleDownload,
  accept = ['image/png', 'image/jpeg', 'application/pdf'],
  maxFileSize,
  minimumImageDimensions = {
    height: 20,
    width: 20,
  },
  onMove,
  onDrop,
  index,
  documentGroupName,
  canDrag,
  projectId,
  document_date,
  isProjectDocumentsPage,
  selectedDocument,
  setSelectedDocument,
  userCanMove,
  canTrackPermit,
  handleTrackPermit,
}) {
  const [uploadState, setUploadState] = useState(UploadState.NOOP);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isShowingErrorModal, setShowingErrorModal] = useState(false);
  const appContext = useContext(AppContext);
  const [checked, setChecked] = useState(false);

  const handleChange = (event) => {
    if (checked === false) {
      setSelectedDocument([
        ...selectedDocument,
        JSON.parse(event.target.value),
      ]);
    } else {
      setSelectedDocument(
        selectedDocument.filter(
          (ele) => ele.id !== JSON.parse(event.target.value).id,
        ),
      );
    }

    setChecked(!checked);
  };

  useEffect(() => {
    if (!selectedDocument.length) {
      setChecked(false);
    }
  }, [selectedDocument.length]);

  const dropzone = useDropzone({
    accept,
    async onDropAccepted(acceptedFiles) {
      const errors = await uploadFileValidation.validate(acceptedFiles[0], {
        maxFileSize: maxFileSize,
        imageDimensions: minimumImageDimensions,
        allowedTypes: accept,
      });

      if (errors.length) {
        setUploadErrors(errors.map((error) => error.message));
        setShowingErrorModal(true);

        return;
      }

      setUploadState(UploadState.WAITING);
      await handleReplace(acceptedFiles.pop());
      setUploadState(UploadState.SUCCESS);
    },
    multiple: false,
  });

  const [showModal, setShowModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [pendingReplace, setPendingReplace] = useState(false);
  const context = useContext(TooltipContext);
  const dropRef = useRef(null);
  const triggerRef = useRef(null);

  const [, drop] = useDrop({
    accept: documentGroupName,
    hover(item, monitor) {
      if (!dropRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      onMove({
        dragIndex,
        hoverIndex,
        document,
        index: item.index,
      });
      item.document = document;
      item.dragIndex = dragIndex;
      item.hoverIndex = hoverIndex;
      item.index = hoverIndex;
    },
    drop({ dragIndex, hoverIndex, index }, monitor) {
      onDrop({
        dragIndex,
        hoverIndex,
        document,
        index,
      });
    },
  });
  const [{ opacity }, drag, preview] = useDrag({
    item: {
      type: documentGroupName,
      index,
      document,
    },
    collect(monitor) {
      return {
        opacity: monitor.isDragging() ? 0 : 1,
      };
    },
  });

  const created = useMemo(
    () => {
      const raw = document.originalCreatedDate
        || document.ProjectUploadModel?.originalCreatedDate
        || document.createdAt
        || document.created_at;
      const d = raw ? new Date(raw) : null;
      return d && !isNaN(d) ? format(d, DATE_FORMAT) : '';
    },
    [document.createdAt, document.created_at, document?.originalCreatedDate, document?.ProjectUploadModel?.originalCreatedDate],
  );
  const updated = useMemo(
    () => {
      const raw = document.updatedAt || document.updated_at;
      const d = raw ? new Date(raw) : null;
      return d && !isNaN(d) ? format(d, DATE_FORMAT) : '';
    },
    [document.updatedAt, document.updated_at],
  );
  const type = useMemo(
    () =>
      document.upload
        ? document.upload.mime_type.match(MIME_TYPE_STRIP_REGEX)[1]
        : document.mime_type.match(MIME_TYPE_STRIP_REGEX)[1],
    [document.mime_type],
  );

  const handleReplaceClick = (e) => {
    e.stopPropagation();
    if (permitStatus === 'Active') {
      setPendingReplace(true);
      setShowReplaceModal(true);
    } else {
      dropzone.open();
    }
  };

  // for permit tracking
  const permitIssuedDate = useMemo(() => {
    const permits = document?.ProjectUploadModel?.permits;
    if (
      Array.isArray(permits) &&
      permits.length > 0 &&
      permits[0]?.issued_date
    ) {
      return format(getUTCTime(permits[0].issued_date), DATE_FORMAT);
    }
    return 'NA';
  }, [document?.ProjectUploadModel?.permits]);

  const permitExpirationDate = useMemo(() => {
    const permits = document?.ProjectUploadModel?.permits;
    if (
      Array.isArray(permits) &&
      permits.length > 0 &&
      permits[0]?.expiryDate
    ) {
      return format(getUTCTime(permits[0].expiryDate), DATE_FORMAT);
    }
    return 'NA';
  }, [document?.ProjectUploadModel?.permits]);

  const permitStatus = useMemo(() => {
    const permits = document?.ProjectUploadModel?.permits;
    if (Array.isArray(permits) && permits.length > 0) {
      // If no expiry date, permit cannot expire
      if (!permits[0].expiryDate) {
        return permits[0].status ? 'Active' : 'Inactive';
      }
      
      if (
        isBefore(
          getUTCTime(permits[0].expiryDate),
          new Date(format(new Date(), DATE_FORMAT)),
        )
      ) {
        return permits[0].status ? 'Expired' : 'Inactive';
      }
      return permits[0].status ? 'Active' : 'Inactive';
    }
    return 'Inactive';
  }, [document?.ProjectUploadModel?.permits]);

  const userCanTrackPermit = canTrackPermit;
  const userCanDownload = canDownload;
  const userCanReplace =
    canReplace && permCheck('update', 'Project Documents Replace');
  const userCanRename = canRename && permCheck('update', label);
  const userCanEdit = canEdit && permCheck('update', label);
  const userCanDelete = canDelete && permCheck('delete', label);

  drop(dropRef);
  drag(triggerRef);

  const docTypesWithCheckboxExcluded = ['Regulations'];

  useEffect(() => {
    if (uploadState === UploadState.SUCCESS) {
      setTimeout(() => {
        setUploadState(UploadState.NOOP);
      }, 1600);
    }
  }, [uploadState]);

  const getColor = (isBorder) => {
    if (documentGroupName === 'Permits' && permitStatus !== 'Inactive') {
      // If no expiration date, use default color
      if (permitExpirationDate === 'NA') {
        return isBorder ? 'none' : '#0d2330';
      }
      
      const today = parse(
        format(new Date(), DATE_FORMAT),
        DATE_FORMAT,
        new Date(),
      );
      const permit = parse(permitExpirationDate, DATE_FORMAT, new Date());
      const future = addDays(today, 46);

      if (isBefore(permit, today)) {
        return isBorder ? '0 0 1px 1px #FF4C51' : '#FF4C51';
      } else if (!isBefore(permit, today) && isBefore(permit, future)) {
        return isBorder ? '0 0 1px 1px #FF9F43' : '#FF9F43';
      } else {
        return isBorder ? 'none' : '#0d2330';
      }
    } else {
      return isBorder ? 'none' : '#0d2330';
    }
  };

  return (
    <>
      <tr
        className='file-row'
        ref={dropRef}
        ref={preview}
        style={{
          opacity,
          borderRadius: '4px',
          boxShadow: getColor(true),
        }}
      >
        {isProjectDocumentsPage &&
          userCanMove &&
          !docTypesWithCheckboxExcluded.includes(documentGroupName) && (
            <td className='file-row__column' style={{ width: '50px' }}>
              <input
                value={JSON.stringify(document)}
                onChange={handleChange}
                checked={checked}
                type='checkbox'
              />
            </td>
          )}

        <td
          className='file-row__column file-row__column--fill'
          data-tip={document.name}
          data-delay-show='125'
          data-event='click'
          onMouseLeave={context.hideToolTip}
        >
          {document.upload ? document.upload.name : document.name}
        </td>
        <td className='file-row__column file-row__column--type'>
          {documentGroupName !== 'Permits' ? type : permitStatus}
        </td>
        {document_date || appContext.get('user').roleName === 'Admin' ? (
          <>
            <td className='file-row__column file-row__column--hide-on-mobile file-row__column--auto-width'>
              {created}
            </td>
            {documentGroupName === 'Permits' && (
              <>
                <td className='file-row__column file-row__column--hide-on-mobile file-row__column--auto-width'>
                  {permitIssuedDate}
                </td>
                <td
                  className='file-row__column file-row__column--hide-on-mobile file-row__column--auto-width'
                  style={{ color: getColor(false) }}
                >
                  {permitExpirationDate}
                </td>
              </>
            )}
            {documentGroupName !== 'Permits' && (
              <td className='file-row__column file-row__column--hide-on-mobile file-row__column--auto-width'>
                {updated}
              </td>
            )}
          </>
        ) : null}

        <td className='file-row__column file-row__column--action file-row__column--auto-width'>
          <Dropdown
            label={
              <FontAwesomeIcon
                fixedWidth={true}
                spin={uploadState === UploadState.WAITING}
                icon={
                  uploadState === UploadState.WAITING
                    ? faCircleNotch
                    : uploadState === UploadState.SUCCESS
                    ? faCheckCircle
                    : faBars
                }
              />
            }
            disabled={uploadState === UploadState.WAITING}
          >
            {userCanTrackPermit ? (
              <MenuItem onClick={handleTrackPermit}>
                <FontAwesomeIcon icon={faLocationArrow} fixedWidth /> Track
                Permit
              </MenuItem>
            ) : null}

            {userCanDownload ? (
              <MenuItem
                onClick={
                  type === 'pdf'
                    ? handleDownload
                    : () => {
                        window.open(downloadUrl, '_blank');
                      }
                }
              >
                <FontAwesomeIcon icon={faCloudDownloadAlt} fixedWidth />{' '}
                Download
              </MenuItem>
            ) : null}

            {type === 'pdf' ? (
              <MenuItem onClick={handleEdit}>
                <FontAwesomeIcon
                  icon={userCanEdit ? faEdit : faEye}
                  fixedWidth
                />{' '}
                {userCanEdit ? 'Edit' : 'View'}
              </MenuItem>
            ) : null}

            {userCanRename ? (
              <MenuItem onClick={handleRename}>
                <FontAwesomeIcon icon={faFont} fixedWidth /> Rename
              </MenuItem>
            ) : null}

            {userCanReplace ? (
              <MenuItem onClick={handleReplaceClick}>
                <FontAwesomeIcon icon={faFileImport} fixedWidth /> Replace
                <input {...dropzone.getInputProps()} />
              </MenuItem>
            ) : null}

            {userCanDelete ? (
              <MenuItem onClick={() => setShowModal(true)}>
                <FontAwesomeIcon icon={faTrash} fixedWidth /> Destroy
              </MenuItem>
            ) : null}
          </Dropdown>
        </td>
      </tr>

      <ConfirmationModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleConfirm={() => {
          setShowModal(false);
          handleDelete();
        }}
        title='Are you sure you want to delete this document?'
        subTitle={
          permitStatus === 'Active'
            ? 'This permit file is currently being tracked. Deleting it will also remove the tracking. Are you sure you want to proceed?'
            : 'This can not be undone.'
        }
        buttonText='Delete Document'
      />

      <ConfirmationModal
        show={showReplaceModal}
        handleClose={() => {
          setShowReplaceModal(false);
          setPendingReplace(false);
        }}
        handleConfirm={() => {
          setShowReplaceModal(false);
          if (pendingReplace) {
            dropzone.open(); // Manually trigger file dialog
            setPendingReplace(false);
          }
        }}
        title='Are you sure you want to replace this document?'
        subTitle="You're about to replace a permit file that is currently being tracked. If you continue, you'll need to re-activate the tracking. Do you wish to proceed?"
        buttonText='Replace Document'
      />

      <SuccessModal
        show={isShowingErrorModal}
        handleClose={() => setShowingErrorModal(false)}
        handleSubmit={() => setShowingErrorModal(false)}
        submitBtnText='OK'
        messages={uploadErrors}
        title='Error'
        isAlert
      />
    </>
  );
}

FileRow.propTypes = {
  downloadUrl: PropTypes.string.isRequired,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  canReplace: PropTypes.bool,
  handleDelete: PropTypes.func,
  handleEdit: PropTypes.func,
  handleRename: PropTypes.func,
  handleReplace: PropTypes.func,
  label: PropTypes.string,
  permCheck: PropTypes.func.isRequired,
  document: PropTypes.object,
  isLoading: PropTypes.bool,
  document_date: PropTypes.bool,
  selectedDocument: PropTypes.array,
  setSelectedDocument: PropTypes.func,
  canMove: PropTypes.bool,
  canTrackPermit: PropTypes.bool,
  handleTrackPermit: PropTypes.func,
};
